import React, { useState, useEffect } from 'react';
import { Search, Plus, Download, Upload, Trash2, RefreshCw, Globe, Edit2, Save, X, FileText, CheckCircle } from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { TranslationService, TranslationOverrideDto } from '../../../../services/translationService';
import { SupportedLanguage, LANGUAGE_CONFIG } from '../../../../i18n';

interface TranslationKey {
  key: string;
  nbValue: string;
  svValue: string;
  nbOverride?: string;
  svOverride?: string;
  hasNbOverride: boolean;
  hasSvOverride: boolean;
}

const TranslationAdmin: React.FC = () => {
  const { user } = useAuth();
  const { t, language } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage>('nb');
  const [translations, setTranslations] = useState<TranslationKey[]>([]);
  const [filteredTranslations, setFilteredTranslations] = useState<TranslationKey[]>([]);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [showOnlyOverrides, setShowOnlyOverrides] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [showImportDialog, setShowImportDialog] = useState(false);

  useEffect(() => {
    if (user?.companyNo) {
      loadTranslations();
      loadStats();
    }
  }, [user?.companyNo]);

  useEffect(() => {
    filterTranslations();
  }, [searchTerm, translations, showOnlyOverrides]);

  const loadTranslations = async () => {
    if (!user?.companyNo) return;
    
    try {
      setLoading(true);
      
      // Load default translations
      const [nbDefaults, svDefaults] = await Promise.all([
        TranslationService.loadDefaultTranslations('nb'),
        TranslationService.loadDefaultTranslations('sv')
      ]);

      // Load custom overrides
      const overrides = await TranslationService.getAllTranslationOverrides(user.companyNo);
      
      // Flatten default translations to dot notation
      const nbFlat = flattenObject(nbDefaults);
      const svFlat = flattenObject(svDefaults);
      
      // Create override maps
      const nbOverrideMap = new Map<string, string>();
      const svOverrideMap = new Map<string, string>();
      
      overrides.forEach(override => {
        if (override.languageCode === 'nb') {
          nbOverrideMap.set(override.translationKey, override.translationValue);
        } else if (override.languageCode === 'sv') {
          svOverrideMap.set(override.translationKey, override.translationValue);
        }
      });
      
      // Combine all unique keys
      const allKeys = new Set([...Object.keys(nbFlat), ...Object.keys(svFlat)]);
      
      // Create translation objects
      const translationList: TranslationKey[] = Array.from(allKeys).map(key => ({
        key,
        nbValue: nbFlat[key] || '',
        svValue: svFlat[key] || '',
        nbOverride: nbOverrideMap.get(key),
        svOverride: svOverrideMap.get(key),
        hasNbOverride: nbOverrideMap.has(key),
        hasSvOverride: svOverrideMap.has(key)
      })).sort((a, b) => a.key.localeCompare(b.key));
      
      setTranslations(translationList);
    } catch (error) {
      console.error('Failed to load translations:', error);
      alert('Kunne ikke laste oversettelser');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    if (!user?.companyNo) return;
    
    try {
      const stats = await TranslationService.getTranslationStats(user.companyNo);
      setStats(stats);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const flattenObject = (obj: any, prefix = ''): Record<string, string> => {
    const result: Record<string, string> = {};
    
    for (const key in obj) {
      const newKey = prefix ? `${prefix}.${key}` : key;
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        Object.assign(result, flattenObject(obj[key], newKey));
      } else {
        result[newKey] = String(obj[key]);
      }
    }
    
    return result;
  };

  const filterTranslations = () => {
    let filtered = translations;
    
    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(t => 
        t.key.toLowerCase().includes(search) ||
        t.nbValue.toLowerCase().includes(search) ||
        t.svValue.toLowerCase().includes(search) ||
        (t.nbOverride && t.nbOverride.toLowerCase().includes(search)) ||
        (t.svOverride && t.svOverride.toLowerCase().includes(search))
      );
    }
    
    // Filter by overrides only
    if (showOnlyOverrides) {
      filtered = filtered.filter(t => t.hasNbOverride || t.hasSvOverride);
    }
    
    setFilteredTranslations(filtered);
  };

  const handleEdit = (translationKey: TranslationKey) => {
    setEditingKey(translationKey.key);
    const currentValue = selectedLanguage === 'nb' 
      ? (translationKey.nbOverride || translationKey.nbValue)
      : (translationKey.svOverride || translationKey.svValue);
    setEditValue(currentValue);
  };

  const handleSave = async () => {
    if (!editingKey || !user?.companyNo) return;
    
    try {
      setSavingKey(editingKey);
      
      await TranslationService.saveTranslationOverride(user.companyNo, {
        languageCode: selectedLanguage,
        translationKey: editingKey,
        translationValue: editValue
      });
      
      // Reload translations
      await loadTranslations();
      await loadStats();
      
      setEditingKey(null);
      setEditValue('');
    } catch (error) {
      console.error('Failed to save translation:', error);
      alert('Kunne ikke lagre oversettelse');
    } finally {
      setSavingKey(null);
    }
  };

  const handleDelete = async (translationKey: string) => {
    if (!user?.companyNo) return;
    
    if (!window.confirm(`Er du sikker på at du vil slette oversettelsen for "${translationKey}"?`)) {
      return;
    }
    
    try {
      await TranslationService.deleteTranslationOverride(
        user.companyNo,
        selectedLanguage,
        translationKey
      );
      
      // Reload translations
      await loadTranslations();
      await loadStats();
    } catch (error) {
      console.error('Failed to delete translation:', error);
      alert('Kunne ikke slette oversettelse');
    }
  };

  const handleExport = async () => {
    if (!user?.companyNo) return;
    
    try {
      const blob = await TranslationService.exportTranslations(
        user.companyNo,
        selectedLanguage,
        false
      );
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `translations_${selectedLanguage}_${user.companyNo}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export translations:', error);
      alert('Kunne ikke eksportere oversettelser');
    }
  };

  const handleImport = async () => {
    if (!importFile || !user?.companyNo) return;
    
    try {
      await TranslationService.importTranslations(
        user.companyNo,
        selectedLanguage,
        importFile
      );
      
      // Reload translations
      await loadTranslations();
      await loadStats();
      
      setShowImportDialog(false);
      setImportFile(null);
      alert('Oversettelser importert!');
    } catch (error) {
      console.error('Failed to import translations:', error);
      alert('Kunne ikke importere oversettelser');
    }
  };

  const handleReset = async () => {
    if (!user?.companyNo) return;
    
    if (!window.confirm(`Er du sikker på at du vil tilbakestille alle oversettelser for ${selectedLanguage === 'nb' ? 'norsk' : 'svensk'}?`)) {
      return;
    }
    
    try {
      await TranslationService.resetTranslations(user.companyNo, selectedLanguage);
      
      // Reload translations
      await loadTranslations();
      await loadStats();
      
      alert('Oversettelser tilbakestilt!');
    } catch (error) {
      console.error('Failed to reset translations:', error);
      alert('Kunne ikke tilbakestille oversettelser');
    }
  };

  const getDisplayValue = (translation: TranslationKey, lang: SupportedLanguage) => {
    if (lang === 'nb') {
      return translation.nbOverride || translation.nbValue;
    } else {
      return translation.svOverride || translation.svValue;
    }
  };

  const hasOverride = (translation: TranslationKey, lang: SupportedLanguage) => {
    return lang === 'nb' ? translation.hasNbOverride : translation.hasSvOverride;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Globe className="h-8 w-8 text-blue-500" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Oversettelsesadministrasjon</h1>
              <p className="text-sm text-gray-600">Administrer tilpassede oversettelser for din organisasjon</p>
            </div>
          </div>
          <button
            onClick={loadTranslations}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Oppdater"
          >
            <RefreshCw className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Statistics */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600">Totalt antall nøkler</div>
              <div className="text-2xl font-bold text-gray-900">{translations.length}</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm text-blue-600">Norske oversettelser</div>
              <div className="text-2xl font-bold text-blue-900">
                {stats.languages?.nb?.overrides || 0}
              </div>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4">
              <div className="text-sm text-yellow-600">Svenske oversettelser</div>
              <div className="text-2xl font-bold text-yellow-900">
                {stats.languages?.sv?.overrides || 0}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Language selector */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Språk:</label>
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value as SupportedLanguage)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="nb">Norsk (Bokmål)</option>
              <option value="sv">Svensk</option>
            </select>
          </div>

          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Søk etter nøkkel eller verdi..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Filter */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showOnlyOverrides}
              onChange={(e) => setShowOnlyOverrides(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Vis kun oversettelser</span>
          </label>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={() => setShowImportDialog(true)}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
            >
              <Upload className="h-4 w-4" />
              Import
            </button>
            <button
              onClick={handleExport}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Eksport
            </button>
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Tilbakestill
            </button>
          </div>
        </div>
      </div>

      {/* Translation list */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nøkkel
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Standard verdi
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tilpasset verdi
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Handlinger
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTranslations.map((translation) => (
                <tr key={translation.key} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                    {translation.key}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {selectedLanguage === 'nb' ? translation.nbValue : translation.svValue}
                  </td>
                  <td className="px-6 py-4">
                    {editingKey === translation.key ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="flex-1 px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          autoFocus
                        />
                        <button
                          onClick={handleSave}
                          disabled={savingKey === translation.key}
                          className="p-1 text-green-600 hover:bg-green-50 rounded"
                        >
                          {savingKey === translation.key ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                          ) : (
                            <Save className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setEditingKey(null);
                            setEditValue('');
                          }}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className={`text-sm ${hasOverride(translation, selectedLanguage) ? 'font-medium text-blue-600' : 'text-gray-400'}`}>
                          {getDisplayValue(translation, selectedLanguage)}
                        </span>
                        {hasOverride(translation, selectedLanguage) && (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(translation)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Rediger"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      {hasOverride(translation, selectedLanguage) && (
                        <button
                          onClick={() => handleDelete(translation.key)}
                          className="text-red-600 hover:text-red-900"
                          title="Slett oversettelse"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Import dialog */}
      {showImportDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">Importer oversettelser</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Velg JSON-fil med oversettelser
              </label>
              <input
                type="file"
                accept=".json"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="w-full"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowImportDialog(false);
                  setImportFile(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Avbryt
              </button>
              <button
                onClick={handleImport}
                disabled={!importFile}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Importer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TranslationAdmin;